import { NoteEditorDialog } from "@/features/reader/notes/NoteEditorDialog";
import { useApi } from "@/hooks/use-api";
import { cn } from "@/lib/utils";
import { useAppDispatch, useAppState } from "@/store/useApp";
import { forwardRef, useEffect, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { BlockQuickActions } from "./BlockQuickActions";
import {
  Block as BlockType,
  SentimentType,
  Suggestion,
  SuggestionStatus,
} from "../../../types/document";
import { BlockActions } from "./BlockActions";
import { BlockContainer } from "./BlockContainer";
import { BlockContent } from "./BlockContent";
import { BlockMarkers } from "./BlockMarkers";
import { BlockReferences } from "./BlockReferences";
import OnboardingAISuggestionHint from "../ onboarding/flows/OnboardingSuggestionHint";
import OnboardingHighlightHintFlow from "../ onboarding/flows/OnboardingHighlightHintFlow";
import OnboardingNoteHintFlow from "../ onboarding/flows/OnboardingNoteHintFlow";
import { config } from "@/config";
import { useFlow } from "@frigade/react";
import { OnboardingSteps } from "../ onboarding/OnboardingGuide";
import { resolveSuggestion } from "./blockUtils";
import { Commentary } from "./Commentary";

interface BlockProps {
  block: BlockType;
  index: number;
  isSelected: boolean;
  isExpanded: boolean;
  focusMode?: boolean;
  suggestion?: Suggestion;
  stripListMarker?: boolean;
  isListItem?: boolean;
}

export const Block = forwardRef<HTMLDivElement, BlockProps>(
  (
    { block, index, isSelected, isExpanded, focusMode = false, suggestion, stripListMarker = false, isListItem = false },
    ref
  ) => {
    const dispatch = useAppDispatch();
    const currentDocument = useAppState((state) => state.currentDocument);
    const selectedGroup = useAppState((state) => state.selectedGroup);
    const classificationFilters = useAppState((state) => state.classificationFilters);
    const { flow } = useFlow(config.frigade.flows.gettingStarted);
    const [onboardingNavigated, setOnboardingNavigated] = useState(false);

    // Helper function to find the next block index with a pending AI suggestion
    const findNextBlockWithPendingSuggestion = (startIndex: number): number => {
      if (!currentDocument || !selectedGroup) return startIndex + 1;

      const blockIds = selectedGroup.blockIds;
      
      // Search from the next index onwards
      for (let i = startIndex + 1; i < blockIds.length; i++) {
        const blockId = blockIds[i];
        const blockSuggestion = resolveSuggestion(currentDocument, blockId, classificationFilters);
        
        if (blockSuggestion && blockSuggestion.status === "pending") {
          return i;
        }
      }
      
      // If no pending suggestion found ahead, fallback to next index
      return startIndex + 1;
    };

    // This should not be needed, but is required to make sequences of actions work.
    // For example, approving a suggestion, then changing the sentiment.
    // State in reducer is correct after approval but not triggering a re-render for some reason.
    const latest = useRef({
      sentiment: block.sentiment,
      suggestionStatus: suggestion?.status,
    });

    useEffect(() => {
      latest.current.sentiment = block.sentiment;
      latest.current.suggestionStatus = suggestion?.status;
    }, [block.sentiment, suggestion?.status]);

    const {
      updateBlock,
      approveSuggestion,
      deleteSuggestion,
      rejectSuggestion,
      updateSuggestion,
    } = useApi();
    const [isNoteEditorOpen, setIsNoteEditorOpen] = useState(false);
    const suggestionStatus = suggestion?.status;
    const isAccepted = suggestionStatus === "approved";
    const isRejected = suggestionStatus === "rejected";

    const handleSelect = () => {
      dispatch({ type: "SET_SELECTED_BLOCK_INDEX", payload: index });
    };

    const handleExpand = () => {
      dispatch({ type: "SET_EXPANDED_BLOCK_ID", payload: block.id });
    };

    const handleCollapse = () => {
      dispatch({ type: "SET_EXPANDED_BLOCK_ID", payload: null });
    };

    const handleAcceptSuggestion = async () => {
      if (!currentDocument || !suggestion?.id) return;

      // Approve suggestion (sets block sentiment as well)
      await approveSuggestion(currentDocument.id, suggestion);
    };

    const handleRejectSuggestion = async () => {
      if (!currentDocument || !suggestion?.id) return;

      try {
        await rejectSuggestion(currentDocument.id, suggestion);
      } catch (error) {
        console.error("Failed to reject suggestion:", error);
      }
    };

    const handleSentimentChange = async (
      sentiment: SentimentType | null,
      toggle: boolean = true
    ) => {
      if (!currentDocument) return;

      if (suggestion) {
        if (latest.current.suggestionStatus === "pending") {
          await rejectSuggestion(currentDocument.id, suggestion);
        } else if (suggestion.classification) {
          await updateSuggestion(currentDocument.id, {
            ...suggestion,
            classification: null,
            status: latest.current.suggestionStatus as SuggestionStatus,
          });
        }
      }

      let newSentiment = sentiment;
      if (toggle) {
        // Toggle behavior: if block already has this sentiment, clear it
        newSentiment =
          latest.current.sentiment === sentiment ? null : sentiment;
      }

      await updateBlock(currentDocument.id, block.id, {
        sentiment: newSentiment,
      });
    };

    const handleEditNote = () => {
      setIsNoteEditorOpen(true);
      dispatch({ type: "SET_NOTE_EDITOR_OPEN", payload: true });
    };

    const handleSaveRemark = async (updatedSuggestion: Suggestion) => {
      if (
        updatedSuggestion.sentiment &&
        updatedSuggestion.sentiment !== "neutral"
      ) {
        await updateBlock(currentDocument!.id, block.id, {
          sentiment: updatedSuggestion.sentiment as SentimentType,
        });
      } else {
        if (
          !updatedSuggestion.id &&
          (updatedSuggestion.sentiment === "neutral" ||
            !updatedSuggestion.sentiment)
        ) {
          // New suggestion and no explicit sentiment, set to neutral
          await updateBlock(currentDocument!.id, block.id, {
            sentiment: "neutral" as SentimentType,
          });
        }
      }

      // Onboarding logic - only execute for onboarding documents
      if (
        currentDocument?.metaType === "onboarding" &&
        flow?.getCurrentStep()?.id as OnboardingSteps === "ai_suggestion" &&
        !onboardingNavigated
      ) {
        // Navigate to next block with pending AI suggestion
        const nextBlockIndex = findNextBlockWithPendingSuggestion(index);
        dispatch({ type: "SET_SELECTED_BLOCK_INDEX", payload: nextBlockIndex });
        setOnboardingNavigated(true);
      }

      setIsNoteEditorOpen(false);
      dispatch({ type: "SET_NOTE_EDITOR_OPEN", payload: false });
    };

    const handleClearSuggestion = async () => {
      if (!currentDocument || !suggestion?.id) return;

      if (suggestion.status === "pending") {
        return;
      }

      await deleteSuggestion(currentDocument.id, suggestion.id);
    };

    // Collapse block
    useHotkeys(
      "left",
      () => {
        if (isSelected && isExpanded) {
          handleCollapse();
        }
      },
      { enabled: isSelected, preventDefault: true },
      [isSelected, isExpanded]
    );

    // Expand block
    useHotkeys(
      "right",
      () => {
        if (isSelected && suggestion && !isExpanded) {
          handleExpand();
        }
      },
      { enabled: isSelected, preventDefault: true },
      [isSelected, isExpanded, suggestion]
    );

    // Edit note
    useHotkeys(
      "n",
      () => {
        if (isSelected) {
          handleEditNote();
        }
      },
      {
        enabled: isSelected,
        preventDefault: true,
      },
      [isSelected]
    );

    // Set positive sentiment
    useHotkeys(
      "a",
      () => {
        if (isSelected) {
          handleSentimentChange("positive");
        }
      },
      { enabled: isSelected },
      [isSelected]
    );

    // Set neutral sentiment
    useHotkeys(
      "s",
      () => {
        if (isSelected) {
          handleSentimentChange("neutral");
        }
      },
      { enabled: isSelected },
      [isSelected]
    );

    // Set negative sentiment
    useHotkeys(
      "d",
      () => {
        if (isSelected) {
          handleSentimentChange("negative");
        }
      },
      { enabled: isSelected },
      [isSelected]
    );

    // Clear sentiment and suggestion
    useHotkeys(
      "backspace",
      () => {
        handleSentimentChange(null);
        handleClearSuggestion();
      },
      { enabled: isSelected },
      [isSelected, suggestion]
    );

    // Accept suggestion
    useHotkeys(
      "tab",
      () => {
        if (isSelected && suggestion && !isAccepted && !isRejected) {
          handleAcceptSuggestion();
        }
      },
      { enabled: isSelected, preventDefault: true },
      [isSelected, suggestion, isAccepted, isRejected]
    );

    const editSuggestion = suggestion ?? {
      blocks: [block.id],
      primaryBlockId: block.id,
      text: "",
      sentiment: "neutral",
      status: "pending",
    };

    const suggestionHint =
      isSelected && suggestion?.status === "pending" ? (
        <OnboardingAISuggestionHint />
      ) : null;

    const highlightHint =
      isSelected && index === 1 ? <OnboardingHighlightHintFlow /> : null;

    const noteHint =
      isSelected && index === 1 ? <OnboardingNoteHintFlow completed={isNoteEditorOpen} /> : null;

    if (block.type === 'agent') {
      return (
        <div className="mr-10" ref={ref} id={`block-${block.id}`}>
          <Commentary block={block} />
        </div>
      );
    }

    return (
      <div
        className={cn("relative group mr-10", {
          "hint-block": index === 0,
          "hint-suggestion": isSelected && suggestion?.status === "pending",
          "hint-highlight": isSelected && index === 1,
          "hint-note": isSelected && index === 1,
        })}
        ref={ref}
        id={`block-${block.id}`}
      >
        {suggestionHint}
        {highlightHint}
        {noteHint}

        <BlockContainer
          block={block}
          suggestion={suggestion}
          isSelected={isSelected}
          isExpanded={isExpanded}
        >
          <div onClick={handleSelect}>
            <BlockMarkers suggestion={suggestion} isSelected={isSelected} isListItem={isListItem} />
            <BlockContent block={block} dimmed={focusMode && !isSelected} stripListMarker={stripListMarker} />
          </div>

          {isSelected && block.type === "text" && !isExpanded && (
            <BlockQuickActions
              visible={true}
              onPositive={() => handleSentimentChange("positive")}
              onNeutral={() => handleSentimentChange("neutral")}
              onNegative={() => handleSentimentChange("negative")}
              onClear={() => {
                handleSentimentChange(null);
                handleClearSuggestion();
              }}
              onEdit={() => handleEditNote()}
            />
          )}
        </BlockContainer>

        {/* Suggestion text */}
        {suggestion?.text &&
          !isRejected &&
          (isExpanded || suggestion?.status !== "pending") && (
            <div
              role="note"
              className={cn(
                "mt-2 mb-3 p-2 rounded-sm text-sm",
                suggestion.status === "pending"
                  ? "bg-purple-50 border border-purple-200 text-purple-600"
                  : "bg-zinc-50 border border-zinc-200 text-secondary-foreground",
                focusMode && !isSelected && "text-gray-300"
              )}
            >
              {suggestion.text}
            </div>
          )}

        {isExpanded &&
          isSelected &&
          suggestion &&
          !isAccepted &&
          !isRejected && (
            <BlockActions
              onAcceptSuggestion={handleAcceptSuggestion}
              onRejectSuggestion={handleRejectSuggestion}
            />
          )}

        {suggestion && (isExpanded || isAccepted) && (
          <BlockReferences blockId={block.id} suggestion={suggestion} focusMode={focusMode} isSelected={isSelected} />
        )}

        <NoteEditorDialog
          isOpen={isNoteEditorOpen}
          onClose={() => {
            setIsNoteEditorOpen(false);
            dispatch({ type: "SET_NOTE_EDITOR_OPEN", payload: false });
          }}
          onSave={handleSaveRemark}
          suggestion={editSuggestion}
          sentiment={
            block.sentiment ?? (editSuggestion.sentiment as SentimentType)
          }
        />
      </div>
    );
  }
);
